"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowDownWideNarrow, ArrowUpNarrowWide, Search, X } from "lucide-react";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { applyCensusQuery, DEFAULT_QUERY, defaultDir, formatScore, isFiltered, MIN_ENTRY_OPTIONS, recommendStatus, serializeCensusQuery, SORT_KEYS, SORT_LABELS, USE_CASE_LABELS, labelForUseCase } from "./census-shared";
import type { CensusModel, CensusQuery, SortKey } from "./census-shared";
import type { ReactNode } from "react";
interface CensusRegistryProps {
  year: number;
  models: CensusModel[];
  vendors: {
    id: string;
    name: string;
  }[];
  vendorMarks: Record<string, ReactNode>;
  initialQuery: CensusQuery;
  minResponses: number;
  modelHrefBase: string;
}
const STATUS_TONES = {
  emerald: "border-emerald-400/60 text-emerald-300",
  amber: "border-amber-400/60 text-amber-300",
  stone: "border-stone-400/50 text-stone-300"
} as const;
const SEGMENTS = [0, 1, 2, 3, 4];
function ScoreMeter(t0) {
  const $ = _c(4);
  const {
    value
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = SEGMENTS.map(segment => {
      const fill = Math.max(0, Math.min(1, value - segment));
      return <span key={segment} className="relative h-1.5 w-3 overflow-hidden rounded-[2px] bg-white/10"><span className="absolute inset-y-0 left-0 bg-emerald-400" style={{
          width: `${fill * 100}%`
        }} /></span>;
    });
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div aria-hidden="true" className="flex gap-0.5">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function ScoreCell(t0) {
  const $ = _c(17);
  const {
    value,
    label,
    active
  } = t0;
  const t1 = active && "bg-emerald-400/[0.06]";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("px-3 py-4 align-middle", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== value) {
    t3 = formatScore(value);
    $[2] = value;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== label) {
    t4 = <span className="sr-only"> out of 5 {label}</span>;
    $[4] = label;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <span className="font-mono text-lg font-semibold tabular-nums leading-none">{t3}{t4}</span>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== value) {
    t6 = <ScoreMeter value={value} />;
    $[9] = value;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className="flex flex-col items-end gap-1.5">{t5}{t6}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t2 || $[15] !== t7) {
    t8 = <td className={t2}>{t7}</td>;
    $[14] = t2;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}
function StatusBadge(t0) {
  const $ = _c(18);
  const {
    percent
  } = t0;
  let t1;
  if ($[0] !== percent) {
    t1 = recommendStatus(percent);
    $[0] = percent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const status = t1;
  const t2 = status.description;
  const t3 = STATUS_TONES[status.tone];
  let t4;
  if ($[2] !== t3) {
    t4 = cn("inline-flex items-center gap-1.5 rounded-sm border px-2 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.2em]", t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = status.tone === "emerald" && "bg-emerald-400";
  const t6 = status.tone === "amber" && "bg-amber-400";
  const t7 = status.tone === "stone" && "bg-stone-400";
  let t8;
  if ($[4] !== t5 || $[5] !== t6 || $[6] !== t7) {
    t8 = cn("h-1.5 w-1.5 rounded-full", t5, t6, t7);
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== t8) {
    t9 = <span aria-hidden="true" className={t8} />;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== status.description) {
    t10 = <span className="sr-only">: {status.description}</span>;
    $[10] = status.description;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== status.description || $[13] !== status.label || $[14] !== t10 || $[15] !== t4 || $[16] !== t9) {
    t11 = <span title={t2} className={t4}>{t9}{status.label}{t10}</span>;
    $[12] = status.description;
    $[13] = status.label;
    $[14] = t10;
    $[15] = t4;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  return t11;
}
function Chip(t0) {
  const $ = _c(8);
  const {
    pressed,
    onClick,
    children,
    className
  } = t0;
  const t1 = pressed ? "border-emerald-700 bg-emerald-700 text-white dark:border-emerald-400 dark:bg-emerald-400 dark:text-emerald-950" : "border-stone-300 bg-background text-foreground hover:border-stone-500 dark:border-stone-700 dark:hover:border-stone-500";
  let t2;
  if ($[0] !== className || $[1] !== t1) {
    t2 = cn("inline-flex h-8 touch-manipulation items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50", t1, className);
    $[0] = className;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children || $[4] !== onClick || $[5] !== pressed || $[6] !== t2) {
    t3 = <button type="button" aria-pressed={pressed} onClick={onClick} className={t2}>{children}</button>;
    $[3] = children;
    $[4] = onClick;
    $[5] = pressed;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
export function CensusRegistry(t0) {
  const $ = _c(118);
  const {
    year,
    models,
    vendors,
    vendorMarks,
    initialQuery,
    minResponses,
    modelHrefBase
  } = t0;
  const pathname = usePathname();
  let t1;
  if ($[0] !== modelHrefBase) {
    t1 = modelId => `${modelHrefBase}/models/${encodeURIComponent(modelId)}`;
    $[0] = modelHrefBase;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const modelHref = t1;
  const [query, setQuery] = useState(initialQuery);
  const firstRender = useRef(true);
  let t2;
  let t3;
  if ($[2] !== pathname || $[3] !== query) {
    t2 = () => {
      if (firstRender.current) {
        firstRender.current = false;
        return;
      }
      window.history.replaceState(window.history.state, "", `${pathname}${serializeCensusQuery(query)}`);
    };
    t3 = [query, pathname];
    $[2] = pathname;
    $[3] = query;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== models || $[7] !== query) {
    t4 = applyCensusQuery(models, query);
    $[6] = models;
    $[7] = query;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const visible = t4;
  let t5;
  if ($[9] !== query) {
    t5 = isFiltered(query);
    $[9] = query;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const filtered = t5;
  const t6 = query.sort;
  const t7 = query.dir;
  let t8;
  if ($[11] !== query.vendors) {
    t8 = query.vendors.join();
    $[11] = query.vendors;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const headerKey = `${t6}-${t7}-${t8}-${query.useCase}-${query.minEntries}`;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = patch => setQuery(prev => ({
      ...prev,
      ...patch
    }));
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const update = t9;
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = sort => setQuery(prev_0 => prev_0.sort === sort ? {
      ...prev_0,
      dir: prev_0.dir === "asc" ? "desc" : "asc"
    } : {
      ...prev_0,
      sort,
      dir: defaultDir(sort)
    });
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const setSort = t10;
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = vendorId => setQuery(prev_1 => ({
      ...prev_1,
      vendors: prev_1.vendors.includes(vendorId) ? prev_1.vendors.filter(v => v !== vendorId) : [...prev_1.vendors, vendorId]
    }));
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const toggleVendor = t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  if ($[16] !== filtered || $[17] !== headerKey || $[18] !== minResponses || $[19] !== modelHref || $[20] !== models.length || $[21] !== query.dir || $[22] !== query.minEntries || $[23] !== query.q || $[24] !== query.sort || $[25] !== query.useCase || $[26] !== query.vendors || $[27] !== vendorMarks || $[28] !== vendors || $[29] !== visible || $[30] !== year) {
    const columns = [{
      key: "value",
      label: "Value",
      align: "right",
      hint: "Value for money"
    }, {
      key: "quality",
      label: "Quality",
      align: "right",
      hint: "Output quality"
    }, {
      key: "speed",
      label: "Speed",
      align: "right",
      hint: "Speed"
    }, {
      key: "recommend",
      label: "Recommend",
      align: "right",
      hint: "Would recommend"
    }, {
      key: "entries",
      label: "Ratings",
      align: "right",
      hint: "Verified ratings"
    }];
    let t17;
    if ($[36] !== query.dir || $[37] !== query.sort) {
      t17 = key => query.sort === key ? query.dir === "asc" ? "ascending" : "descending" : "none";
      $[36] = query.dir;
      $[37] = query.sort;
      $[38] = t17;
    } else {
      t17 = $[38];
    }
    const ariaSort = t17;
    const DirIcon = query.dir === "asc" ? ArrowUpNarrowWide : ArrowDownWideNarrow;
    let t18;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <label htmlFor="census-search" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-stone-600 dark:text-stone-400">Search</label>;
      $[39] = t18;
    } else {
      t18 = $[39];
    }
    let t19;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <Search aria-hidden="true" className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-stone-500" />;
      $[40] = t19;
    } else {
      t19 = $[40];
    }
    let t20;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = event_0 => update({
        q: event_0.target.value
      });
      $[41] = t20;
    } else {
      t20 = $[41];
    }
    let t21;
    if ($[42] !== query.q) {
      t21 = <div className="min-w-[14rem] flex-1">{t18}<div className="relative">{t19}<Input id="census-search" name="q" type="search" spellCheck={false} value={query.q} onChange={t20} placeholder={"Model or vendor\u2026"} autoComplete="off" className="h-9 bg-background pl-9" /></div></div>;
      $[42] = query.q;
      $[43] = t21;
    } else {
      t21 = $[43];
    }
    let t22;
    if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <label htmlFor="census-use-case" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-stone-600 dark:text-stone-400">Use case</label>;
      $[44] = t22;
    } else {
      t22 = $[44];
    }
    const t23 = query.useCase ?? "any";
    let t24;
    if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = value => update({
        useCase: value === "any" ? null : value
      });
      $[45] = t24;
    } else {
      t24 = $[45];
    }
    let t25;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = <SelectTrigger id="census-use-case" className="h-9 min-w-[11rem] bg-background"><SelectValue placeholder="Any use case" /></SelectTrigger>;
      $[46] = t25;
    } else {
      t25 = $[46];
    }
    let t26;
    if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
      t26 = <SelectContent><SelectItem value="any">Any use case</SelectItem>{Object.entries(USE_CASE_LABELS).map(_temp2)}</SelectContent>;
      $[47] = t26;
    } else {
      t26 = $[47];
    }
    let t27;
    if ($[48] !== t23) {
      t27 = <div>{t22}<Select value={t23} onValueChange={t24}>{t25}{t26}</Select></div>;
      $[48] = t23;
      $[49] = t27;
    } else {
      t27 = $[49];
    }
    let t28;
    if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = <legend className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-stone-600 dark:text-stone-400">Ratings</legend>;
      $[50] = t28;
    } else {
      t28 = $[50];
    }
    let t29;
    if ($[51] !== minResponses || $[52] !== query.minEntries) {
      t29 = MIN_ENTRY_OPTIONS.map(option => <Chip key={option} pressed={query.minEntries === option} onClick={() => update({
        minEntries: option
      })} className="h-9 rounded-md px-2.5 font-mono">{option === 0 ? `${minResponses}+` : `${option}+`}</Chip>);
      $[51] = minResponses;
      $[52] = query.minEntries;
      $[53] = t29;
    } else {
      t29 = $[53];
    }
    let t30;
    if ($[54] !== t29) {
      t30 = <fieldset>{t28}<div className="flex gap-1">{t29}</div></fieldset>;
      $[54] = t29;
      $[55] = t30;
    } else {
      t30 = $[55];
    }
    let t31;
    if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = <label htmlFor="census-sort" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-stone-600 dark:text-stone-400">Sort by</label>;
      $[56] = t31;
    } else {
      t31 = $[56];
    }
    let t32;
    if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = value_1 => {
        const sort_0 = value_1 as SortKey;
        update({
          sort: sort_0,
          dir: defaultDir(sort_0)
        });
      };
      $[57] = t32;
    } else {
      t32 = $[57];
    }
    let t33;
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = <SelectTrigger id="census-sort" className="h-9 min-w-[9rem] bg-background"><SelectValue /></SelectTrigger>;
      $[58] = t33;
    } else {
      t33 = $[58];
    }
    let t34;
    if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
      t34 = <SelectContent>{SORT_KEYS.map(_temp3)}</SelectContent>;
      $[59] = t34;
    } else {
      t34 = $[59];
    }
    let t35;
    if ($[60] !== query.sort) {
      t35 = <div>{t31}<Select value={query.sort} onValueChange={t32}>{t33}{t34}</Select></div>;
      $[60] = query.sort;
      $[61] = t35;
    } else {
      t35 = $[61];
    }
    const t36 = query.dir === "asc" ? "Sorted ascending. Switch to descending" : "Sorted descending. Switch to ascending";
    let t37;
    if ($[62] !== query.dir) {
      t37 = () => update({
        dir: query.dir === "asc" ? "desc" : "asc"
      });
      $[62] = query.dir;
      $[63] = t37;
    } else {
      t37 = $[63];
    }
    let t38;
    if ($[64] !== DirIcon) {
      t38 = <DirIcon aria-hidden="true" />;
      $[64] = DirIcon;
      $[65] = t38;
    } else {
      t38 = $[65];
    }
    let t39;
    if ($[66] !== t36 || $[67] !== t37 || $[68] !== t38) {
      t39 = <Button type="button" variant="outline" size="icon" aria-label={t36} onClick={t37}>{t38}</Button>;
      $[66] = t36;
      $[67] = t37;
      $[68] = t38;
      $[69] = t39;
    } else {
      t39 = $[69];
    }
    let t40;
    if ($[70] !== t35 || $[71] !== t39) {
      t40 = <div className="flex items-end gap-1 md:hidden">{t35}{t39}</div>;
      $[70] = t35;
      $[71] = t39;
      $[72] = t40;
    } else {
      t40 = $[72];
    }
    let t41;
    if ($[73] !== filtered || $[74] !== query.dir || $[75] !== query.sort) {
      t41 = filtered && <Button type="button" variant="ghost" size="sm" className="h-9" onClick={() => setQuery({
        ...DEFAULT_QUERY,
        sort: query.sort,
        dir: query.dir
      })}><X aria-hidden="true" />Reset filters</Button>;
      $[73] = filtered;
      $[74] = query.dir;
      $[75] = query.sort;
      $[76] = t41;
    } else {
      t41 = $[76];
    }
    let t42;
    if ($[77] !== t21 || $[78] !== t27 || $[79] !== t30 || $[80] !== t40 || $[81] !== t41) {
      t42 = <div className="flex flex-wrap items-end gap-3">{t21}{t27}{t30}{t40}{t41}</div>;
      $[77] = t21;
      $[78] = t27;
      $[79] = t30;
      $[80] = t40;
      $[81] = t41;
      $[82] = t42;
    } else {
      t42 = $[82];
    }
    let t43;
    if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
      t43 = <legend className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-stone-600 dark:text-stone-400">Vendor</legend>;
      $[83] = t43;
    } else {
      t43 = $[83];
    }
    let t44;
    if ($[84] !== query.vendors || $[85] !== vendorMarks || $[86] !== vendors) {
      let t45;
      if ($[88] !== query.vendors || $[89] !== vendorMarks) {
        t45 = vendor => <Chip key={vendor.id} pressed={query.vendors.includes(vendor.id)} onClick={() => toggleVendor(vendor.id)}><span className="inline-flex h-4 w-4 items-center justify-center [&>*]:h-4 [&>*]:w-4">{vendorMarks[vendor.id]}</span>{vendor.name}</Chip>;
        $[88] = query.vendors;
        $[89] = vendorMarks;
        $[90] = t45;
      } else {
        t45 = $[90];
      }
      t44 = vendors.map(t45);
      $[84] = query.vendors;
      $[85] = vendorMarks;
      $[86] = vendors;
      $[87] = t44;
    } else {
      t44 = $[87];
    }
    let t45;
    if ($[91] !== t44) {
      t45 = <fieldset className="mt-4">{t43}<div className="flex flex-wrap gap-1.5">{t44}</div></fieldset>;
      $[91] = t44;
      $[92] = t45;
    } else {
      t45 = $[92];
    }
    if ($[93] !== t42 || $[94] !== t45) {
      t15 = <form role="search" aria-label="Filter and sort the registry" onSubmit={_temp} className="rounded-2xl border border-stone-300/90 bg-stone-50/80 p-4 dark:border-stone-700 dark:bg-stone-900/40">{t42}{t45}</form>;
      $[93] = t42;
      $[94] = t45;
      $[95] = t15;
    } else {
      t15 = $[95];
    }
    const t46 = visible.length;
    const t47 = models.length;
    const t48 = filtered ? " \xB7 filters applied" : "";
    const t49 = SORT_LABELS[query.sort];
    let t50;
    if ($[96] !== t49) {
      t50 = t49.toLowerCase();
      $[96] = t49;
      $[97] = t50;
    } else {
      t50 = $[97];
    }
    const t51 = query.dir === "asc" ? "ascending" : "descending";
    let t52;
    if ($[98] !== models.length || $[99] !== t48 || $[100] !== t50 || $[101] !== t51 || $[102] !== visible.length) {
      t52 = <span>Showing {t46} of {t47} models{t48} · sorted by{" "}{t50}{" "}{t51}</span>;
      $[98] = models.length;
      $[99] = t48;
      $[100] = t50;
      $[101] = t51;
      $[102] = visible.length;
      $[103] = t52;
    } else {
      t52 = $[103];
    }
    let t53;
    if ($[104] !== year) {
      t53 = <span className="tracking-[0.2em]">DOC. CS-{year}</span>;
      $[104] = year;
      $[105] = t53;
    } else {
      t53 = $[105];
    }
    if ($[106] !== t52 || $[107] !== t53) {
      t16 = <p aria-live="polite" className="mt-4 mb-3 flex flex-wrap items-baseline justify-between gap-2 font-mono text-xs text-stone-600 dark:text-stone-400">{t52}{t53}</p>;
      $[106] = t52;
      $[107] = t53;
      $[108] = t16;
    } else {
      t16 = $[108];
    }
    t12 = "census-board overflow-hidden rounded-2xl border border-white/10 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.7)]";
    if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3 sm:px-5"><p className="flex items-center gap-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-[#ece7d9]"><span aria-hidden="true" className="h-2 w-2 rounded-full bg-emerald-400 motion-safe:animate-pulse" />Departures · Coding models</p><p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#b3bcb6]">Rank = registry position by value · refreshed every 5 min</p></div>;
      $[109] = t13;
    } else {
      t13 = $[109];
    }
    t14 = visible.length === 0 ? <div className="px-6 py-16 text-center"><p className="font-mono text-sm uppercase tracking-[0.3em] text-[#ece7d9]">No models match</p><p className="mt-2 text-sm text-[#b3bcb6]">Loosen a filter or clear the search to bring the board back.</p><Button type="button" variant="outline" size="sm" className="mt-5 border-white/20 bg-transparent text-[#ece7d9] hover:bg-white/10 hover:text-white" onClick={() => setQuery({
        ...DEFAULT_QUERY,
        sort: query.sort,
        dir: query.dir
      })}>Reset filters</Button></div> : <><div className="hidden overflow-x-auto md:block"><table className="w-full min-w-[56rem] border-collapse text-[#ece7d9]"><caption className="sr-only">The {year} DevPass Model Census registry. Scores are 1–5 averages from verified DevPass developers.</caption><thead><tr className="border-b border-white/10 font-mono text-[11px] uppercase tracking-[0.2em] text-[#cfd6d1]"><th scope="col" className="px-3 py-3 text-left sm:pl-5"><span className="sr-only">Registry rank</span><span aria-hidden="true">#</span></th><th scope="col" aria-sort={ariaSort("name")} className="px-3 py-3 text-left"><button type="button" onClick={() => setSort("name")} className={cn("-mx-1 inline-flex min-h-6 items-center gap-1.5 rounded-sm px-1 uppercase tracking-[0.2em] outline-none hover:text-white focus-visible:ring-2 focus-visible:ring-emerald-400", query.sort === "name" && "text-white")}>Model{query.sort === "name" && <DirIcon aria-hidden="true" className="h-3.5 w-3.5" />}</button></th>{columns.map(column => <th key={column.key} scope="col" aria-sort={ariaSort(column.key)} className={cn("px-3 py-3 text-right", query.sort === column.key && "bg-emerald-400/[0.06]")}><button type="button" onClick={() => setSort(column.key)} title={`Sort by ${column.hint.toLowerCase()}`} className={cn("-mx-1 inline-flex min-h-6 items-center gap-1.5 rounded-sm px-1 uppercase tracking-[0.2em] outline-none hover:text-white focus-visible:ring-2 focus-visible:ring-emerald-400", query.sort === column.key && "text-white")}>{column.label}{query.sort === column.key && <DirIcon aria-hidden="true" className="h-3.5 w-3.5" />}</button></th>)}<th scope="col" className="px-3 py-3 text-right sm:pr-5">Status</th></tr></thead><tbody key={headerKey}>{visible.map((model, index) => <tr key={model.modelId} className={cn("border-b border-white/[0.07] transition-colors last:border-b-0 hover:bg-white/[0.04]", index < 12 && "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 motion-safe:fill-mode-both")} style={index < 12 ? {
              animationDelay: `${index * 45}ms`
            } : undefined}><td className="px-3 py-4 align-middle sm:pl-5"><span className={cn("inline-flex h-9 w-9 items-center justify-center rounded-full font-mono text-sm font-bold tabular-nums", model.rank === 1 ? "border-[3px] border-double border-emerald-400/80 text-emerald-300" : model.rank <= 3 ? "border border-emerald-400/50 text-emerald-200" : "border border-dashed border-white/20 text-[#cfd6d1]")}>{model.rank}</span></td><th scope="row" className="px-3 py-4 text-left align-middle font-normal"><div className="flex items-center gap-3"><span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white/[0.06] text-[#ece7d9] [&>*]:h-5 [&>*]:w-5">{vendorMarks[model.vendorId]}</span><div className="min-w-0"><a href={modelHref(model.modelId)} className="font-display block truncate rounded-sm text-base font-semibold leading-tight outline-none hover:underline focus-visible:ring-2 focus-visible:ring-emerald-400">{model.name}</a><div className="mt-0.5 truncate font-mono text-[11px] text-[#b3bcb6]">{model.vendorName} · {model.modelId}{model.topUseCase ? ` · mostly ${labelForUseCase(model.topUseCase).toLowerCase()}` : ""}</div></div></div></th><ScoreCell value={model.avgValueScore} label="value" active={query.sort === "value"} /><ScoreCell value={model.avgQualityScore} label="quality" active={query.sort === "quality"} /><ScoreCell value={model.avgSpeedScore} label="speed" active={query.sort === "speed"} /><td className={cn("px-3 py-4 text-right align-middle font-mono text-lg font-semibold tabular-nums", query.sort === "recommend" && "bg-emerald-400/[0.06]")}>{model.recommendPercent}%</td><td className={cn("px-3 py-4 text-right align-middle font-mono text-base tabular-nums text-[#cfd6d1]", query.sort === "entries" && "bg-emerald-400/[0.06]")}>{model.responseCount}</td><td className="px-3 py-4 text-right align-middle sm:pr-5"><StatusBadge percent={model.recommendPercent} /></td></tr>)}</tbody></table></div><ul key={`m-${headerKey}`} className="divide-y divide-white/[0.07] md:hidden">{visible.map((model_0, index_0) => <li key={model_0.modelId} className={cn("px-4 py-4 text-[#ece7d9]", index_0 < 8 && "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 motion-safe:fill-mode-both")} style={index_0 < 8 ? {
          animationDelay: `${index_0 * 45}ms`
        } : undefined}><div className="flex items-center gap-3"><span className={cn("inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-mono text-sm font-bold tabular-nums", model_0.rank === 1 ? "border-[3px] border-double border-emerald-400/80 text-emerald-300" : "border border-dashed border-white/20 text-[#cfd6d1]")}>{model_0.rank}</span><div className="min-w-0 flex-1"><h3 className="font-display truncate text-base font-semibold leading-tight"><a href={modelHref(model_0.modelId)} className="rounded-sm outline-none hover:underline focus-visible:ring-2 focus-visible:ring-emerald-400">{model_0.name}</a></h3><p className="mt-0.5 truncate font-mono text-[11px] text-[#b3bcb6]">{model_0.vendorName} · {model_0.responseCount} ratings</p></div><div className="text-right"><p className="font-mono text-xl font-bold tabular-nums leading-none">{model_0.recommendPercent}%</p><p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#b3bcb6]">recommend</p></div></div><dl className="mt-3 grid grid-cols-3 gap-2">{[{
              label: "Value",
              value: model_0.avgValueScore
            }, {
              label: "Quality",
              value: model_0.avgQualityScore
            }, {
              label: "Speed",
              value: model_0.avgSpeedScore
            }].map(_temp4)}</dl><div className="mt-3 flex items-center justify-between gap-2"><span className="truncate font-mono text-[11px] text-[#b3bcb6]">{model_0.topUseCase ? `Mostly ${labelForUseCase(model_0.topUseCase).toLowerCase()}` : "Use case n/a"}</span><StatusBadge percent={model_0.recommendPercent} /></div></li>)}</ul></>;
    $[16] = filtered;
    $[17] = headerKey;
    $[18] = minResponses;
    $[19] = modelHref;
    $[20] = models.length;
    $[21] = query.dir;
    $[22] = query.minEntries;
    $[23] = query.q;
    $[24] = query.sort;
    $[25] = query.useCase;
    $[26] = query.vendors;
    $[27] = vendorMarks;
    $[28] = vendors;
    $[29] = visible;
    $[30] = year;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
  } else {
    t12 = $[31];
    t13 = $[32];
    t14 = $[33];
    t15 = $[34];
    t16 = $[35];
  }
  let t17;
  if ($[110] !== t12 || $[111] !== t13 || $[112] !== t14) {
    t17 = <div className={t12}>{t13}{t14}</div>;
    $[110] = t12;
    $[111] = t13;
    $[112] = t14;
    $[113] = t17;
  } else {
    t17 = $[113];
  }
  let t18;
  if ($[114] !== t15 || $[115] !== t16 || $[116] !== t17) {
    t18 = <div>{t15}{t16}{t17}</div>;
    $[114] = t15;
    $[115] = t16;
    $[116] = t17;
    $[117] = t18;
  } else {
    t18 = $[117];
  }
  return t18;
}
function _temp4(score) {
  return <div key={score.label} className="rounded-md bg-white/[0.05] px-2.5 py-2"><dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#b3bcb6]">{score.label}</dt><dd className="mt-1 flex items-center justify-between gap-2"><span className="font-mono text-base font-semibold tabular-nums">{formatScore(score.value)}</span><ScoreMeter value={score.value} /></dd></div>;
}
function _temp3(key_0) {
  return <SelectItem key={key_0} value={key_0}>{SORT_LABELS[key_0]}</SelectItem>;
}
function _temp2(t0) {
  const [value_0, label] = t0;
  return <SelectItem key={value_0} value={value_0}>{label}</SelectItem>;
}
function _temp(event) {
  return event.preventDefault();
}